import axios from "axios";

const API = "/api";

// ── auth plumbing ──────────────────────────────────────────────────────────────
// Every request carries two credentials:
//
//   Authorization: Bearer <clerk session jwt>   proves "signed in with Google"
//   X-MFA-Token:   <step-up token>              proves "authenticator code passed"
//
// The Clerk session token is short-lived and rotated by Clerk, so it is fetched
// fresh on each request through a provider registered by the auth context rather
// than cached here.
//
// The step-up token lives in sessionStorage, not localStorage: it is a bearer
// credential, and sessionStorage drops it when the tab closes, which keeps a
// stolen one useful for a much shorter window.

const MFA_STORAGE_KEY = "yb_mfa_token";

let sessionTokenProvider = async () => null;
let authChallengeHandler = null;

export function setSessionTokenProvider(fn) {
  sessionTokenProvider = typeof fn === "function" ? fn : async () => null;
}

/** Called when the server says the second factor is missing or stale. */
export function setAuthChallengeHandler(fn) {
  authChallengeHandler = typeof fn === "function" ? fn : null;
}

export function getMfaToken() {
  try {
    return sessionStorage.getItem(MFA_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

export function setMfaToken(token) {
  try {
    if (token) sessionStorage.setItem(MFA_STORAGE_KEY, token);
    else sessionStorage.removeItem(MFA_STORAGE_KEY);
  } catch {
    /* private browsing / storage disabled — the token simply won't persist */
  }
}

// Every request is bounded. Without this a stalled call leaves the UI sitting on
// a spinner forever with no way back — far worse than a visible error.
const REQUEST_TIMEOUT_MS = 20000;
const TOKEN_TIMEOUT_MS = 10000;

/** Fetching the session token happens *before* the request, so axios's own
 *  timeout does not cover it. Bound it separately or a wedged token provider
 *  hangs the request forever. */
async function sessionTokenWithTimeout() {
  let timer;
  try {
    return await Promise.race([
      sessionTokenProvider(),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error("token timeout")), TOKEN_TIMEOUT_MS);
      }),
    ]);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Build an axios instance that carries the operator's credentials.
 *
 * Exported so the legacy Gold-Hunter API layer uses the *same* credentials and
 * the same challenge handling rather than a bare, unauthenticated axios. Both
 * backends enforce the identical gate, so both clients must present identical
 * proof.
 */
export function createAuthedClient(baseURL) {
  const client = axios.create({ baseURL, timeout: REQUEST_TIMEOUT_MS });

  client.interceptors.request.use(async (config) => {
    const token = await sessionTokenWithTimeout();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    const mfa = getMfaToken();
    if (mfa) config.headers["X-MFA-Token"] = mfa;
    return config;
  });

  client.interceptors.response.use(
    (res) => res,
    (error) => {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const code = detail && typeof detail === "object" ? detail.code : null;
      if (status === 403 && (code === "mfa_required" || code === "mfa_enrolment_required")) {
        // The step-up token is gone or expired — drop it and let the gate re-prompt.
        setMfaToken("");
        if (authChallengeHandler) authChallengeHandler(code);
      }
      return Promise.reject(error);
    }
  );

  return client;
}

const http = createAuthedClient(API);

/** Pull a readable message out of a FastAPI error response. */
export function errorMessage(error, fallback = "Something went wrong.") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  if (error?.code === "ECONNABORTED") return "The server took too long to respond. Try again.";
  if (error?.message === "Network Error") return "Could not reach the server.";
  return fallback;
}

async function get(path, params) {
  const res = await http.get(path, { params });
  return res.data;
}
async function post(path, body) {
  const res = await http.post(path, body || {});
  return res.data;
}
async function put(path, body) {
  const res = await http.put(path, body || {});
  return res.data;
}
async function del(path) {
  const res = await http.delete(path);
  return res.data;
}

// ── auth / settings ──
export const getAuthConfig = () => get("/auth/config");
export const getAuthSession = () => get("/auth/session");
export const startMfaEnrolment = () => post("/auth/mfa/start");
export const confirmMfaEnrolment = (code) => post("/auth/mfa/confirm", { code });
export const verifyMfaCode = (code) => post("/auth/mfa/verify", { code });
export const getSettings = () => get("/settings");

// ── dashboard ──
export const getDashboard = () => get("/dashboard");

// ── treasury ──
export const getTreasury = () => get("/treasury");
export const syncTreasury = () => post("/treasury/sync");

// ── clients ──
export const listClients = () => get("/clients");
export const createClient = (data) => post("/clients", data);
export const updateClient = (id, data) => put(`/clients/${id}`, data);
export const deleteClient = (id) => del(`/clients/${id}`);

// ── pipeline ──
export const listPipeline = () => get("/pipeline");
export const createPipeline = (data) => post("/pipeline", data);
export const updatePipeline = (id, data) => put(`/pipeline/${id}`, data);
export const deletePipeline = (id) => del(`/pipeline/${id}`);

// ── crew (agents) ──
export const listAgents = () => get("/agents");

// ── jobs (approval queue) ──
export const listJobs = (state) => get("/jobs", state ? { state } : undefined);
export const createJob = (data) => post("/jobs", data);
export const approveJob = (id, approvedBy) => post(`/jobs/${id}/approve`, { approved_by: approvedBy });
export const rejectJob = (id) => post(`/jobs/${id}/reject`);
export const runJob = (id) => post(`/jobs/${id}/run`);
export const completeJob = (id) => post(`/jobs/${id}/complete`);
export const deleteJob = (id) => del(`/jobs/${id}`);

// ── route-llm (drafting only) ──
export const routeLLM = (agentId, prompt) => post("/route-llm", { agent_id: agentId, prompt });

// ── catalog / checkout ──
export const getCatalog = () => get("/catalog");
export const createCheckout = (data) => post("/checkout", data);

// ── watched public addresses (read-only balance watching) ──
// Only ever a PUBLIC address goes over the wire. No key, no seed, no signing.
export const listWatchedAddresses = () => get("/holdings/addresses");
export const addWatchedAddress = (data) => post("/holdings/addresses", data);
export const removeWatchedAddress = (id) => del(`/holdings/addresses/${id}`);
